import axios from 'axios';
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { ppaUrls } from 'config/urls';
import {
    buildKpiDeltaRequestBody,
    mergeKpisFromDelta,
    parseTotalFromPayload,
    type KpiDeltaPage,
    type KpiTreeNode,
    type PerformanceSummaryFeatureMeta,
    type PerformanceSummaryUserInput,
} from './performanceSummaryKpisWithDeltaHelpers';
import type {
    PerformanceSummaryDrilldownFilter,
    PerformanceSummaryOrderByItem,
} from './usePerformanceSummaryKpis';

type KpiDeltaFetchConfig = {
    anchorKpiId: string;
    dimension?: string;
    aggregateAttributeTypes: string[];
    aggregateDimensions?: Array<string | undefined>;
    aggregateIsAttributes?: Array<boolean | undefined>;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
    orderBy?: PerformanceSummaryOrderByItem[] | null;
    pageSize: number;
    timePeriod: { start_timestamp?: number; end_timestamp?: number };
    filters: { dim1_id_list: unknown; dim2_id_list: unknown };
    userInputs?: PerformanceSummaryUserInput[];
    featureMeta?: PerformanceSummaryFeatureMeta;
    normalizeData?: (rows: unknown[]) => unknown[];
};

type UseKpiDeltaNetworkResult<TNode> = {
    pages: KpiDeltaPage<TNode>[];
    pagesRef: MutableRefObject<KpiDeltaPage<TNode>[]>;
    knownKpiIds: Set<string>;
    knownKpiIdsRef: MutableRefObject<Set<string>>;
    inFlightKpiIdsRef: MutableRefObject<Set<string>>;
    generationRef: MutableRefObject<number>;
    isFetchingNextPage: boolean;
    activeFetchCount: number;
    runBulk: (kpiIdsToSend: string[], offset: number, mode: 'replace' | 'append') => Promise<void>;
    runDelta: (addedKpiIds: string[]) => Promise<void>;
};

export function useKpiDeltaNetwork<TNode>(config: KpiDeltaFetchConfig): UseKpiDeltaNetworkResult<TNode> {
    const {
        anchorKpiId,
        dimension,
        aggregateAttributeTypes,
        aggregateDimensions,
        aggregateIsAttributes,
        drilldownFilters,
        orderBy,
        pageSize,
        timePeriod,
        filters,
        userInputs,
        featureMeta,
        normalizeData,
    } = config;

    const [pages, setPages] = useState<KpiDeltaPage<TNode>[]>([]);
    const [knownKpiIds, setKnownKpiIds] = useState<Set<string>>(new Set());
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const [activeFetchCount, setActiveFetchCount] = useState(0);

    const generationRef = useRef(0);
    const pagesRef = useRef<KpiDeltaPage<TNode>[]>([]);
    const knownKpiIdsRef = useRef<Set<string>>(new Set());
    const inFlightKpiIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        pagesRef.current = pages;
    }, [pages]);

    useEffect(() => {
        knownKpiIdsRef.current = knownKpiIds;
    }, [knownKpiIds]);

    const normalizeTreeNodes = useCallback(
        (rows: unknown): KpiTreeNode[] => {
            const list = Array.isArray(rows) ? rows : [];
            const normalized = normalizeData ? normalizeData(list) : list;
            return normalized as KpiTreeNode[];
        },
        [normalizeData],
    );

    const performPost = useCallback(
        async (offset: number, kpiIdsToSend: string[]) => {
            const response = await axios.post(
                ppaUrls.getPerformanceSummaryKpisUrl,
                buildKpiDeltaRequestBody({
                    kpiIds: kpiIdsToSend,
                    dimension,
                    aggregateAttributeTypes,
                    aggregateDimensions,
                    aggregateIsAttributes,
                    drilldownFilters,
                    orderBy,
                    limit: pageSize,
                    offset,
                    timePeriod,
                    filters,
                    userInputs,
                    featureMeta,
                }),
            );
            const payload = response.data as Record<string, unknown> | undefined;
            const nodes = normalizeTreeNodes(Array.isArray(payload?.data) ? payload?.data : []);
            const total = parseTotalFromPayload(payload);
            return { nodes, total };
        },
        [
            dimension,
            aggregateAttributeTypes,
            aggregateDimensions,
            aggregateIsAttributes,
            drilldownFilters,
            orderBy,
            pageSize,
            timePeriod,
            filters,
            userInputs,
            featureMeta,
            normalizeTreeNodes,
        ],
    );

    const runBulk = useCallback(
        async (kpiIdsToSend: string[], offset: number, mode: 'replace' | 'append') => {
            if (!kpiIdsToSend.length) return;
            const currentGeneration = generationRef.current;
            setActiveFetchCount((count) => count + 1);
            if (mode === 'append') setIsFetchingNextPage(true);
            try {
                const { nodes, total } = await performPost(offset, kpiIdsToSend);
                if (generationRef.current !== currentGeneration) return;
                const newPage: KpiDeltaPage<TNode> = { nodes: nodes as TNode[], total, offset, limit: pageSize };
                if (mode === 'replace') {
                    setPages([newPage]);
                    pagesRef.current = [newPage];
                } else {
                    setPages((prev) => {
                        const withoutSameOffset = prev.filter((page) => page.offset !== offset);
                        const next = [...withoutSameOffset, newPage].sort((a, b) => a.offset - b.offset);
                        pagesRef.current = next;
                        return next;
                    });
                }
                const sentSet = new Set(kpiIdsToSend);
                setKnownKpiIds((prev) => {
                    const next = mode === 'replace' ? sentSet : new Set([...prev, ...sentSet]);
                    knownKpiIdsRef.current = next;
                    return next;
                });
            } finally {
                if (generationRef.current === currentGeneration && mode === 'append') {
                    setIsFetchingNextPage(false);
                }
                setActiveFetchCount((count) => Math.max(0, count - 1));
            }
        },
        [performPost, pageSize],
    );

    const runDelta = useCallback(
        async (addedKpiIds: string[]) => {
            if (!addedKpiIds.length) return;
            const offsetsToFetch = pagesRef.current.map((page) => page.offset);
            if (!offsetsToFetch.length) return;
            const currentGeneration = generationRef.current;
            const addedKpiIdSet = new Set(addedKpiIds);
            addedKpiIds.forEach((id) => inFlightKpiIdsRef.current.add(id));
            const totalRequests = addedKpiIds.length * offsetsToFetch.length;
            setActiveFetchCount((count) => count + totalRequests);
            try {
                await Promise.all(
                    addedKpiIds.flatMap((newKpiId) =>
                        offsetsToFetch.map(async (offset) => {
                            const kpiIdsToSend =
                                newKpiId === anchorKpiId ? [anchorKpiId] : [anchorKpiId, newKpiId];
                            const { nodes } = await performPost(offset, kpiIdsToSend);
                            if (generationRef.current !== currentGeneration) return;
                            setPages((prev) => {
                                const next = prev.map((page) =>
                                    page.offset === offset
                                        ? {
                                              ...page,
                                              nodes: mergeKpisFromDelta(
                                                  page.nodes as unknown as KpiTreeNode[],
                                                  nodes as KpiTreeNode[],
                                                  addedKpiIdSet,
                                              ) as unknown as TNode[],
                                          }
                                        : page,
                                );
                                pagesRef.current = next;
                                return next;
                            });
                        }),
                    ),
                );
                if (generationRef.current === currentGeneration) {
                    setKnownKpiIds((prev) => {
                        const next = new Set(prev);
                        addedKpiIds.forEach((id) => next.add(id));
                        knownKpiIdsRef.current = next;
                        return next;
                    });
                }
            } finally {
                addedKpiIds.forEach((id) => inFlightKpiIdsRef.current.delete(id));
                setActiveFetchCount((count) => Math.max(0, count - totalRequests));
            }
        },
        [anchorKpiId, performPost],
    );

    return {
        pages,
        pagesRef,
        knownKpiIds,
        knownKpiIdsRef,
        inFlightKpiIdsRef,
        generationRef,
        isFetchingNextPage,
        activeFetchCount,
        runBulk,
        runDelta,
    };
}
